/**
 * « Ce document n'a pas pu être rouvert » — le troisième état, rendu à l'écran
 * (#1332).
 *
 * Deux surfaces, complémentaires et jamais simultanées, parce que l'avocat peut
 * rencontrer ce verdict dans deux situations qui n'ont pas le même écran :
 *
 * - **la carte** ({@link CanvasRestaurationIndisponible}) occupe l'emplacement
 *   VIDE de `AgentV3View`, quand la restauration a échoué avant tout montage. Il
 *   n'y a alors aucun panneau, et sans elle le refus n'aurait littéralement nulle
 *   part où s'afficher. Le clic humain déploie cet emplacement sans acquérir de
 *   session : le `CanvasSlot` reste monté à côté, mais aucune page blanche ne
 *   peut être créée (interblocage payé le 2026-09-09, 25 parcours agent rouges
 *   quand le refus remplaçait `RedactionCanvas`).
 * - **le bandeau** ({@link CanvasRestaurationIndisponibleBandeau}) s'insère EN
 *   FLUX dans `RedactionPanel` lorsqu'un résident déjà sûr devient illisible
 *   après son montage. Il pousse le document, il ne le recouvre pas et ne
 *   remplace rien : la session SuperDoc reste disponible.
 *
 * Le bouton rejoue l'intention, il ne « recharge » rien : c'est la même
 * orchestration, avec la même décision, sur le même fil. Une tentative qui
 * aboutit lève le verdict, donc fait disparaître la surface qui le portait.
 */

import * as React from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { executerLIntentionDocumentaire } from '../state/canvasDocumentOrchestration';
import type { RestaurationIndisponible } from '../state/canvasRestaurationDeBrouillon';

export const TITRE_RESTAURATION_INDISPONIBLE = 'Ce document n’a pas pu être rouvert';

function useNouvelleTentative(conversationId: string): {
  readonly enCours: boolean;
  readonly reessayer: () => void;
} {
  const [enCours, setEnCours] = React.useState(false);
  const reessayer = React.useCallback(() => {
    setEnCours(true);
    void executerLIntentionDocumentaire({
      kind: 'restaurer-le-brouillon',
      conversationId,
    }).finally(() => setEnCours(false));
  }, [conversationId]);
  return { enCours, reessayer };
}

function BoutonReessayer({
  enCours, reessayer, className,
}: { enCours: boolean; reessayer: () => void; className: string }): React.JSX.Element {
  return (
    <Button variant="outline" size="sm" className={className} onClick={reessayer} disabled={enCours}>
      <RotateCcw className="h-3.5 w-3.5" />
      {enCours ? 'Nouvelle tentative…' : 'Réessayer'}
    </Button>
  );
}

/** La carte, pour l'emplacement vide de l'écran. */
export function CanvasRestaurationIndisponible({
  etat,
}: { etat: RestaurationIndisponible }): React.JSX.Element {
  const { enCours, reessayer } = useNouvelleTentative(etat.conversationId);

  return (
    <div
      role="status"
      data-testid="canvas-restauration-indisponible"
      className="m-6 rounded-lg border border-amber-300 bg-amber-50 p-6 text-sm text-amber-900"
    >
      <p className="font-medium">{TITRE_RESTAURATION_INDISPONIBLE}</p>
      <p className="mt-2 leading-relaxed">{etat.message}</p>
      <BoutonReessayer enCours={enCours} reessayer={reessayer} className="mt-4 gap-1.5" />
    </div>
  );
}

/**
 * Le bandeau, pour un panneau que l'avocat a ouvert lui-même.
 *
 * Même mécanique de flux que `CanvasEcritureBandeau` — et pour la même raison :
 * rien de positionné ne doit entrer dans `RedactionCanvas`, qui porte le
 * `compactMeasurementSelector` du peintre SuperDoc.
 */
export function CanvasRestaurationIndisponibleBandeau({
  etat,
}: { etat: RestaurationIndisponible }): React.JSX.Element {
  const { enCours, reessayer } = useNouvelleTentative(etat.conversationId);

  return (
    <div
      role="status"
      data-testid="canvas-restauration-indisponible-bandeau"
      className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900"
    >
      <div className="flex-1 leading-relaxed">
        <span className="font-medium">{TITRE_RESTAURATION_INDISPONIBLE}. </span>
        <span>{etat.message}</span>
      </div>
      <BoutonReessayer enCours={enCours} reessayer={reessayer} className="shrink-0 gap-1.5 h-7" />
    </div>
  );
}
